import { useCallback, useEffect, useRef, useState } from 'react'
import {
  listMapPrints,
  nextPollDelayMs,
  startMapPrint,
  type FetchImpl,
  type MapPrintJobView,
  type StartMapPrintArgs,
} from './mapPrintClient'

/**
 * Owns one user's print jobs for as long as the print surface is open: loads
 * them, starts new ones, and polls while any of them is still rendering.
 *
 * The load-on-mount is the whole durability story from the client's side. Nobody
 * polls while the user is away — the job's token lives server-side and the status
 * endpoint is idempotent — so signing back in and opening the print panel is what
 * resolves a print that finished overnight.
 *
 * Polling stops as soon as nothing is pending, which matters because the interval
 * is deliberately brisk at first: an A4 is usually done before the user has
 * finished reading the panel.
 */
export interface UseMapPrintJobsOptions {
  /**
   * False where a print could not succeed anyway — no signed-in token, or a
   * ground with no centre to frame a sheet around. The feature is then entirely
   * inert, including the initial listing, so a surface that cannot print costs
   * nothing.
   */
  enabled: boolean
  getAuthToken?: () => Promise<string | null>
  fetchImpl?: FetchImpl
}

export interface UseMapPrintJobsResult {
  jobs: MapPrintJobView[]
  busy: boolean
  error: string | null
  start: (args: StartMapPrintArgs) => Promise<MapPrintJobView | null>
  refresh: () => Promise<void>
}

export function useMapPrintJobs({
  enabled,
  getAuthToken,
  fetchImpl,
}: UseMapPrintJobsOptions): UseMapPrintJobsResult {
  const [jobs, setJobs] = useState<MapPrintJobView[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Held in a ref so the polling effect does not re-subscribe every time the
  // caller re-renders with a fresh closure.
  const deps = useRef({ getAuthToken, fetchImpl })
  deps.current = { getAuthToken, fetchImpl }

  const refresh = useCallback(async () => {
    if (!enabled) return
    setJobs(await listMapPrints(deps.current))
  }, [enabled])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const pendingCount = jobs.filter((j) => j.status === 'Pending').length

  useEffect(() => {
    if (!enabled || pendingCount === 0) return

    let cancelled = false
    let attempt = 0
    let timer: ReturnType<typeof setTimeout>

    const tick = async () => {
      const next = await listMapPrints(deps.current)
      if (cancelled) return
      setJobs(next)
      attempt += 1
      if (next.some((j) => j.status === 'Pending')) schedule()
    }

    const schedule = () => {
      timer = setTimeout(() => void tick(), nextPollDelayMs(attempt))
    }

    schedule()
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [enabled, pendingCount])

  const start = useCallback(
    async (args: StartMapPrintArgs): Promise<MapPrintJobView | null> => {
      setBusy(true)
      setError(null)
      try {
        const result = await startMapPrint(args, deps.current)
        if (result.job) {
          // Inserted rather than re-listed: the new job is the one the user is
          // waiting for, and it should appear the moment the server accepts it.
          setJobs((current) => [result.job!, ...current.filter((j) => j.id !== result.job!.id)])
          return result.job
        }
        setError(result.error ?? null)
        return null
      } finally {
        setBusy(false)
      }
    },
    [],
  )

  return { jobs, busy, error, start, refresh }
}
